import { TextKey, DataTestId } from '@mews-commander/enums';
import { HOSPITALITY_CLOUD_TERMS_AND_CONDITIONS_URL, LocalizedText, MEWS_PRIVACY_POLICY_URL } from '@mews/framework';
import { Button, Stack } from '@optimus-web/core';
import { getStrictCurrentEnterpriseId, isLoggedInSession, useCurrentSession } from '@mews-commander/core';
import {
    LocalizedPageTitle,
    LocalizedParagraph,
    NewTabLink,
    OptimusProvider,
    OverlayedLoader,
    Redirect,
} from '@mews-commander/ui';
import { GeneralArea, GeneralSignInMethod, useRepositoryMutation } from '@mews-commander/data-access';
import { useCommanderThunkDispatch } from '@mews-commander/utils';
import { NewPublicFormWrapper } from '../style';
import {
    getDeviceFingerprintPayload,
    INITIAL_SIGN_IN_PARAMS,
    checkForHardDeviceApproval,
    initializeUserSession,
} from '../../ducks/actions';

export const Consent = () => {
    const dispatch = useCommanderThunkDispatch();
    const currentSession = useCurrentSession();

    const signUpMutation = useRepositoryMutation('generalUsers', 'signUp');
    const signInMutation = useRepositoryMutation('generalUsers', 'signIn');

    const isAcceptingTerms = signUpMutation.isPending || signInMutation.isPending;

    const signInWithConsent = async () => {
        try {
            await signUpMutation.mutateAsync({});
            await signInMutation
                .mutateAsync({
                    ...INITIAL_SIGN_IN_PARAMS,
                    device: getDeviceFingerprintPayload(),
                    method: GeneralSignInMethod.SignInToken,
                    application: GeneralArea.Commander,
                })
                .then(async (data) => dispatch(checkForHardDeviceApproval(data)))
                .then(async (data) => dispatch(initializeUserSession(data)));
            // eslint-disable-next-line @mews/no-empty-catch
        } catch (error) {
            // No action needed here, error handling is done in the mutation
        }
    };

    if (isLoggedInSession(currentSession)) {
        return (
            <Redirect
                to="/$currentEnterpriseId/Dashboard/Index"
                params={{
                    currentEnterpriseId: getStrictCurrentEnterpriseId(),
                }}
            />
        );
    }

    return (
        <NewPublicFormWrapper>
            <OverlayedLoader show={isAcceptingTerms} />
            <OptimusProvider density="spacious">
                <Stack spacing="500" vertical>
                    <Stack spacing="100" vertical>
                        <LocalizedPageTitle textKey={TextKey.UpdatedTermsAndConditionsTitle} />
                        <LocalizedParagraph
                            textKey={TextKey.UpdatedTermsAndConditionsText}
                            substitutes={{
                                termsAndConditionsLink: (linkText) => (
                                    <NewTabLink url={HOSPITALITY_CLOUD_TERMS_AND_CONDITIONS_URL}>{linkText}</NewTabLink>
                                ),
                                privacyPolicyLink: (linkText) => (
                                    <NewTabLink url={MEWS_PRIVACY_POLICY_URL}>{linkText}</NewTabLink>
                                ),
                            }}
                            variant="secondary"
                        />
                    </Stack>
                    <Button
                        variant="primary"
                        onClick={signInWithConsent}
                        data-test-id={DataTestId.UserConsentButton}
                        disabled={isAcceptingTerms}
                        wide
                        type="submit"
                    >
                        <LocalizedText textKey={TextKey.Accept} />
                    </Button>
                </Stack>
            </OptimusProvider>
        </NewPublicFormWrapper>
    );
};
